import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';
import {
  Dialog,
  Button,
  Input,
  Label,
} from '../components/ui';
import { Lock, Eye, EyeOff } from 'lucide-react';

/**
 * PasswordChangeForm Component
 * 
 * Form for changing user password.
 * Validates current password and new password strength.
 *
 * @param {boolean} isOpen - Dialog open state
 * @param {Function} onClose - Close dialog handler
 * @returns {JSX.Element} Password change form dialog
 */
export default function PasswordChangeForm({ isOpen, onClose }) {
  const toast = useToast();
  const { changePassword } = useAuth();

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Validate form
  const validate = useCallback(() => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'მიმდინარე პაროლი აუცილებელია';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'ახალი პაროლი აუცილებელია';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'პაროლი უნდა იყოს მინიმუმ 6 სიმბოლო';
    } else if (formData.newPassword === formData.currentPassword) {
      newErrors.newPassword = 'ახალი პაროლი უნდა განსხვავდებოდეს მიმდინარესგან';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'დაადასტურეთ პაროლი';
    } else if (formData.confirmPassword !== formData.newPassword) {
      newErrors.confirmPassword = 'პაროლები არ ემთხვევა';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Handle field change
  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  // Handle submit
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      await changePassword(formData.currentPassword, formData.newPassword);
      onClose();
      // Reset form
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setErrors({});
    } catch (error) {
      // Error handled in useAuth hook
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validate, changePassword, onClose]);

  if (!isOpen) return null;

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="პაროლის შეცვლა">
      <form onSubmit={handleSubmit} className="space-y-4" aria-label="პაროლის შეცვლის ფორმა">
        {/* Current Password */}
        <div className="space-y-2">
          <Label htmlFor="current-password">მიმდინარე პაროლი *</Label>
          <div className="relative">
            <Input
              id="current-password"
              type={showCurrentPassword ? 'text' : 'password'}
              value={formData.currentPassword}
              onChange={(e) => handleFieldChange('currentPassword', e.target.value)}
              placeholder="••••••••"
              autoFocus
              className="pr-10"
              aria-required="true"
              aria-invalid={!!errors.currentPassword}
              aria-describedby={errors.currentPassword ? 'current-password-error' : undefined}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              aria-label={showCurrentPassword ? 'დამალე პაროლი' : 'აჩვენე პაროლი'}
            >
              {showCurrentPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          </div>
          {errors.currentPassword && (
            <p id="current-password-error" role="alert" className="text-red-500 text-xs">
              {errors.currentPassword}
            </p>
          )}
        </div>

        {/* New Password */}
        <div className="space-y-2">
          <Label htmlFor="new-password">ახალი პაროლი *</Label>
          <div className="relative">
            <Input
              id="new-password"
              type={showNewPassword ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={(e) => handleFieldChange('newPassword', e.target.value)}
              placeholder="••••••••"
              className="pr-10"
              aria-required="true"
              aria-invalid={!!errors.newPassword}
              aria-describedby={errors.newPassword ? 'new-password-error' : 'new-password-help'}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
              onClick={() => setShowNewPassword(!showNewPassword)}
              aria-label={showNewPassword ? 'დამალე პაროლი' : 'აჩვენე პაროლი'}
            >
              {showNewPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          </div>
          {errors.newPassword ? (
            <p id="new-password-error" role="alert" className="text-red-500 text-xs">
              {errors.newPassword}
            </p>
          ) : (
            <span id="new-password-help" className="text-xs text-muted-foreground">
              მინიმუმ 6 სიმბოლო
            </span>
          )}
        </div>

        {/* Confirm Password */}
        <div className="space-y-2">
          <Label htmlFor="confirm-password">დაადასტურეთ პაროლი *</Label>
          <Input
            id="confirm-password"
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
            placeholder="••••••••"
            aria-required="true"
            aria-invalid={!!errors.confirmPassword}
            aria-describedby={errors.confirmPassword ? 'confirm-password-error' : undefined}
          />
          {errors.confirmPassword && (
            <p id="confirm-password-error" role="alert" className="text-red-500 text-xs">
              {errors.confirmPassword}
            </p>
          )}
        </div>

        {/* Security Notice */}
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="flex items-start gap-2">
            <Lock className="h-4 w-4 text-blue-500 mt-0.5" />
            <p className="text-xs text-blue-700 dark:text-blue-300">
              პაროლი დაშიფრულია და ინახება უსაფრთხოდ. ცვლილების შემდეგ მოგიწევთ ხელახლა შესვლა ყველა მოწყობილობაზე.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            aria-label="გაუქმება"
          >
            გაუქმება
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? 'იცვლება...' : 'პაროლის შეცვლა'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

PasswordChangeForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
